import React, { useEffect, useMemo, useState } from "react";

import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  BellRing,
  CheckCircle2,
  Clock3,
  Eye,
  Filter,
  Landmark,
  Layers3,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  TrendingUp,
  X,
} from "lucide-react";

import TitleButtons from "../../../../components/TitleButtons";
import { useTheme } from "../../../../context/ThemeContext";
import { useAuth } from "../../../../context/auth-context";

/* ============================================================================
 * TYPES
 * ========================================================================== */

type TipoAtivo = "ACAO" | "FII" | "ETF" | "BDR" | "OUTRO";

type StatusMonitoramento = "ativo" | "pausado";

type FonteCotacao = "YAHOO_FINANCE";

type HistoricoEvento = {
  id: number;
  data: string;
  titulo: string;
  descricao: string;
  responsavel: string;
};

type AtivoMonitorado = {
  id: number;

  empresaId: number;
  empresaNome: string;

  ticker: string;
  symbolYahoo: string;

  nome: string;
  nomeCurto: string | null;

  tipo: TipoAtivo;

  tipoInstrumentoYahoo: string | null;

  mercado: string;

  exchange: string | null;
  exchangeCompleto: string | null;

  moeda: string;

  preco: number | null;
  fechamentoAnterior: number | null;

  variacao: number | null;
  variacaoPercentual: number | null;

  abertura: number | null;
  maximaDia: number | null;
  minimaDia: number | null;

  volume: number | null;

  maxima52Semanas: number | null;
  minima52Semanas: number | null;

  horarioMercado: string | null;
  timezone: string | null;

  consultadoEm: string | null;

  fonte: FonteCotacao;

  statusMonitoramento: StatusMonitoramento;

  observacao: string | null;

  alertaAcima: number | null;
  alertaAbaixo: number | null;

  historico: HistoricoEvento[];
};

type FilterState = {
  busca: string;
  tipo: string;
  statusMonitoramento: string;
};

type NovoAtivoForm = {
  ticker: string;
  tipo: TipoAtivo;
  observacao: string;
  alertaAcima: string;
  alertaAbaixo: string;
};

type YahooQuote = {
  ticker: string;
  symbol_yahoo: string;

  nome?: string | null;
  nome_curto?: string | null;

  tipo_instrumento?: string | null;

  mercado?: string | null;

  exchange?: string | null;
  exchange_completo?: string | null;

  moeda?: string | null;

  preco?: number | null;
  fechamento_anterior?: number | null;

  variacao?: number | null;
  variacao_percentual?: number | null;

  abertura?: number | null;
  maxima_dia?: number | null;
  minima_dia?: number | null;

  volume?: number | null;

  maxima_52_semanas?: number | null;
  minima_52_semanas?: number | null;

  horario_mercado?: string | null;

  timezone?: string | null;

  consultado_em?: string | null;

  fonte?: FonteCotacao;
};

type YahooQuoteError = {
  ticker?: string;
  symbol_yahoo?: string;
  codigo?: string;
  message?: string;
};

type YahooQuoteApiResponse = {
  ok: boolean;

  parcial?: boolean;

  fonte?: FonteCotacao;

  mercado?: string;

  consultados?: number;
  sucessos?: number;
  falhas?: number;

  results?: YahooQuote[];

  errors?: YahooQuoteError[];

  message?: string;
  erro?: string;

  meta?: {
    empresa_id?: number | string | null;
    usuario_id?: number | string | null;
    endereco_id?: number | string | null;
    departamento_id?: number | string | null;

    yahoo_endpoint?: string;

    interval?: string;
    range?: string;

    consultado_em?: string;
  };
};

type AtivosMonitoradosLayoutProps = {
  section?: string;
};

/* ============================================================================
 * CONSTANTS
 * ========================================================================== */

const DEPARTMENT_COLOR = "#2f3f59";

const INITIAL_NEW_ASSET_FORM: NovoAtivoForm = {
  ticker: "",
  tipo: "ACAO",
  observacao: "",
  alertaAcima: "",
  alertaAbaixo: "",
};

const initialFilters: FilterState = {
  busca: "",
  tipo: "",
  statusMonitoramento: "",
};

const ACCESS_TOKEN_KEYS = ["accessToken", "eva:accessToken", "access_token"];

/* ============================================================================
 * HELPERS
 * ========================================================================== */

function normalizeTextSearch(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function normalizeSlug(value: unknown) {
  return normalizeTextSearch(value).replace(/\s+/g, "-");
}

function getStoredAccessToken(): string {
  try {
    for (const key of ACCESS_TOKEN_KEYS) {
      const value = localStorage.getItem(key);

      if (value && value.trim()) {
        return value.trim();
      }
    }
  } catch {
    // ignora
  }

  return "";
}

function buildAuthenticatedHeaders(): HeadersInit {
  const accessToken = getStoredAccessToken();

  return {
    "Content-Type": "application/json",
    Accept: "application/json",

    ...(accessToken
      ? {
          Authorization: `Bearer ${accessToken}`,
        }
      : {}),
  };
}

function toNumberOrNull(value: unknown): number | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : null;
}

function parseNumberInput(value: string): number | null {
  const clean = String(value || "")
    .trim()
    .replace(",", ".");

  if (!clean) {
    return null;
  }

  const parsed = Number(clean);

  return Number.isFinite(parsed) ? parsed : null;
}

function buildSymbolYahoo(ticker: string) {
  const clean = String(ticker || "")
    .trim()
    .toUpperCase();

  if (!clean) {
    return "";
  }

  if (clean.endsWith(".SA")) {
    return clean;
  }

  return `${clean}.SA`;
}

function normalizeTicker(ticker: string) {
  const clean = String(ticker || "")
    .trim()
    .toUpperCase();

  if (clean.endsWith(".SA")) {
    return clean.slice(0, -3);
  }

  return clean;
}

function formatCurrency(value: number | null, currency = "BRL") {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatInteger(value: number | null) {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }

  return new Intl.NumberFormat("pt-BR", {
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercent(value: number | null) {
  if (value === null || Number.isNaN(value)) {
    return "—";
  }

  return (
    new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value) + "%"
  );
}

function formatDateTime(value: string | null) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function getTipoAtivoLabel(tipo: TipoAtivo) {
  switch (tipo) {
    case "ACAO":
      return "Ação";

    case "FII":
      return "FII";

    case "ETF":
      return "ETF";

    case "BDR":
      return "BDR";

    case "OUTRO":
    default:
      return "Outro";
  }
}

function getStatusMonitoramentoLabel(status: StatusMonitoramento) {
  return status === "ativo" ? "Ativo" : "Pausado";
}

function inferTipoAtivoFromYahoo(instrumentType?: string | null): TipoAtivo {
  const type = String(instrumentType || "")
    .trim()
    .toUpperCase();

  if (type === "ETF") {
    return "ETF";
  }

  if (type === "EQUITY") {
    return "ACAO";
  }

  return "OUTRO";
}

function getStatusMonitoramentoVisual(status: StatusMonitoramento) {
  if (status === "ativo") {
    return {
      background: "#ecfdf3",
      border: "#b7ebc6",
      color: "#166534",
      icon: <CheckCircle2 size={14} />,
    };
  }

  return {
    background: "#fff7ed",
    border: "#fed7aa",
    color: "#9a3412",
    icon: <Clock3 size={14} />,
  };
}

function getVariacaoVisual(variacaoPercentual: number | null) {
  if (variacaoPercentual === null || Number.isNaN(variacaoPercentual)) {
    return {
      background: "#f8fafc",
      border: "#dbe4ef",
      color: "#475569",
      icon: <Activity size={14} />,
    };
  }

  if (variacaoPercentual > 0) {
    return {
      background: "#ecfdf3",
      border: "#b7ebc6",
      color: "#166534",
      icon: <ArrowUpRight size={14} />,
    };
  }

  if (variacaoPercentual < 0) {
    return {
      background: "#fef2f2",
      border: "#fecaca",
      color: "#991b1b",
      icon: <ArrowDownRight size={14} />,
    };
  }

  return {
    background: "#f8fafc",
    border: "#dbe4ef",
    color: "#475569",
    icon: <Activity size={14} />,
  };
}

function isAlertaDisparado(ativo: AtivoMonitorado) {
  if (ativo.preco === null) {
    return false;
  }

  if (ativo.alertaAcima !== null && ativo.preco >= ativo.alertaAcima) {
    return true;
  }

  if (ativo.alertaAbaixo !== null && ativo.preco <= ativo.alertaAbaixo) {
    return true;
  }

  return false;
}

function hexToRgbComponents(hex: string | undefined): [number, number, number] {
  const normalized = (hex || DEPARTMENT_COLOR).replace("#", "");

  const safeHex =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized.padEnd(6, "0").slice(0, 6);

  const int = Number.parseInt(safeHex, 16);

  return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function toRgba(hex: string | undefined, alpha = 1) {
  const [r, g, b] = hexToRgbComponents(hex);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function createLocalId() {
  return Date.now() + Math.floor(Math.random() * 1000);
}

/* ============================================================================
 * COMPONENTES PEQUENOS
 * ========================================================================== */

const Badge: React.FC<{
  children: React.ReactNode;
  background: string;
  color: string;
  border: string;
  icon?: React.ReactNode;
}> = ({ children, background, color, border, icon }) => {
  return (
    <span
      style={{
        ...styles.badge,
        background,
        color,
        borderColor: border,
      }}
    >
      {icon}
      {children}
    </span>
  );
};

const SummaryCard: React.FC<{
  label: string;
  value: string | number;
  helper: string;
  tone: "neutral" | "success" | "warning" | "danger";
  icon: React.ReactNode;
}> = ({ label, value, helper, tone, icon }) => {
  const iconStyle =
    tone === "success"
      ? styles.summaryIconSuccess
      : tone === "warning"
      ? styles.summaryIconWarning
      : tone === "danger"
      ? styles.summaryIconDanger
      : styles.summaryIcon;

  return (
    <div style={styles.summaryCard}>
      <div style={iconStyle}>{icon}</div>

      <div style={styles.summaryInfo}>
        <span style={styles.summaryLabel}>{label}</span>

        <strong style={styles.summaryValue}>{value}</strong>

        <span style={styles.summaryHelper}>{helper}</span>
      </div>
    </div>
  );
};

const Detail: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => {
  return (
    <div style={styles.detailItem}>
      <span style={styles.detailLabel}>{label}</span>

      <strong style={styles.detailValue}>{value}</strong>
    </div>
  );
};

const AtivoRow: React.FC<{
  ativo: AtivoMonitorado;
  departmentColor: string;
  onOpen: (ativo: AtivoMonitorado) => void;
}> = ({ ativo, departmentColor, onOpen }) => {
  const [hovered, setHovered] = useState(false);

  const variacaoVisual = getVariacaoVisual(ativo.variacaoPercentual);

  const statusVisual = getStatusMonitoramentoVisual(ativo.statusMonitoramento);

  const alertaAtivo = isAlertaDisparado(ativo);

  return (
    <tr
      style={{
        ...styles.tr,

        background: hovered ? toRgba(departmentColor, 0.045) : "#ffffff",

        boxShadow: hovered
          ? `inset 3px 0 0 ${departmentColor}`
          : "inset 0 0 0 transparent",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={() => onOpen(ativo)}
      title="Duplo clique para abrir o detalhamento"
    >
      <td style={styles.td}>
        <div style={styles.assetCell}>
          <div
            style={{
              ...styles.assetIcon,

              color: hovered ? departmentColor : "#475569",

              borderColor: hovered ? toRgba(departmentColor, 0.3) : "#dbe4ef",

              background: hovered ? toRgba(departmentColor, 0.07) : "#f8fafc",
            }}
          >
            <BarChart3 size={15} />
          </div>

          <div style={styles.assetInfo}>
            <strong
              style={{
                ...styles.assetTicker,

                color: hovered ? departmentColor : "#111827",
              }}
            >
              {ativo.ticker}
            </strong>

            <span style={styles.mutedText}>{ativo.nome}</span>

            <span style={styles.smallMeta}>
              {getTipoAtivoLabel(ativo.tipo)}
              {" · "}
              {ativo.exchangeCompleto || ativo.exchange || ativo.mercado}
            </span>
          </div>
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{ativo.symbolYahoo}</div>

        <div style={styles.mutedText}>
          {ativo.tipoInstrumentoYahoo || "Não informado"}
        </div>
      </td>

      <td style={styles.td}>
        <div style={styles.valueText}>
          {formatCurrency(ativo.preco, ativo.moeda)}
        </div>

        <div style={styles.mutedText}>
          Fech. ant. {formatCurrency(ativo.fechamentoAnterior, ativo.moeda)}
        </div>
      </td>

      <td style={styles.td}>
        <Badge
          background={variacaoVisual.background}
          color={variacaoVisual.color}
          border={variacaoVisual.border}
          icon={variacaoVisual.icon}
        >
          {ativo.variacaoPercentual === null
            ? "Sem cotação"
            : `${formatPercent(ativo.variacaoPercentual)} (${formatCurrency(
                ativo.variacao,
                ativo.moeda
              )})`}
        </Badge>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>
          {formatCurrency(ativo.minimaDia, ativo.moeda)}

          {" / "}

          {formatCurrency(ativo.maximaDia, ativo.moeda)}
        </div>

        <div style={styles.mutedText}>mínima / máxima</div>
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>{formatInteger(ativo.volume)}</div>

        <div style={styles.mutedText}>volume</div>
      </td>

      <td style={styles.td}>
        <Badge
          background={statusVisual.background}
          color={statusVisual.color}
          border={statusVisual.border}
          icon={statusVisual.icon}
        >
          {getStatusMonitoramentoLabel(ativo.statusMonitoramento)}
        </Badge>
      </td>

      <td style={styles.td}>
        {alertaAtivo ? (
          <Badge
            background="#fff7ed"
            color="#9a3412"
            border="#fed7aa"
            icon={<BellRing size={14} />}
          >
            Alerta
          </Badge>
        ) : (
          <span style={styles.mutedText}>Sem disparo</span>
        )}
      </td>

      <td style={styles.td}>
        <div style={styles.mainText}>Yahoo Finance</div>

        <div style={styles.mutedText}>{formatDateTime(ativo.consultadoEm)}</div>
      </td>

      <td style={styles.tdNoRightBorder}>
        <button
          type="button"
          style={styles.inlineIconButton}
          onClick={() => onOpen(ativo)}
          title="Visualizar ativo"
        >
          <Eye size={16} />
        </button>
      </td>
    </tr>
  );
};

/* ============================================================================
 * COMPONENT PRINCIPAL
 * ========================================================================== */

const InvestimentosAtivosMonitoradosLayout: React.FC<
  AtivosMonitoradosLayoutProps
> = () => {
  /* ==========================================================================
   * CONTEXTOS
   * ======================================================================== */

  const { departmentColor } = useTheme();

  const {
    sessao,
    loading: authLoading,
    error: authError,
    refresh: refreshAuth,
    apiBase,
  } = useAuth();

  const activeDepartmentColor = departmentColor || DEPARTMENT_COLOR;

  /* ==========================================================================
   * CONTEXTO DA SESSÃO
   * ======================================================================== */

  const empresaId = useMemo(() => {
    return (
      toNumberOrNull(sessao?.empresa_ativa_id) ??
      toNumberOrNull(sessao?.empresa_id) ??
      toNumberOrNull(sessao?.empresa?.id)
    );
  }, [sessao]);

  const empresaNome = useMemo(() => {
    return (
      sessao?.empresa?.nome_fantasia ||
      sessao?.empresa?.nome ||
      sessao?.empresa_nome_fantasia ||
      sessao?.empresa_nome ||
      sessao?.empresa_razao_social ||
      "Empresa"
    );
  }, [sessao]);

  const enderecoId = useMemo(() => {
    const selecionado = toNumberOrNull(sessao?.endereco_selecionado_id);

    if (selecionado !== null) {
      return selecionado;
    }

    const primeiroEndereco = sessao?.enderecos_disponiveis?.[0]?.id;

    return toNumberOrNull(primeiroEndereco);
  }, [sessao]);

  const enderecoSelecionado = useMemo(() => {
    if (!enderecoId) {
      return null;
    }

    return (
      sessao?.enderecos_com_modulos?.find(
        (endereco) => Number(endereco.id) === enderecoId
      ) ||
      sessao?.enderecos_disponiveis?.find(
        (endereco) => Number(endereco.id) === enderecoId
      ) ||
      null
    );
  }, [sessao, enderecoId]);

  const departamentosDoContexto = useMemo(() => {
    const peloEndereco = (enderecoSelecionado as any)?.departamentos_acessiveis;

    if (Array.isArray(peloEndereco) && peloEndereco.length) {
      return peloEndereco;
    }

    return sessao?.departamentos_acessiveis || [];
  }, [enderecoSelecionado, sessao]);

  const departamentoFinanceiro = useMemo(() => {
    return (
      departamentosDoContexto.find((dep: any) => {
        const slug = normalizeSlug(dep?.slug);

        const routeKey = normalizeSlug(dep?.routeKey ?? dep?.route_key);

        return (
          slug === "financeiro" ||
          slug === "financas" ||
          routeKey === "financeiro" ||
          routeKey === "financas"
        );
      }) || null
    );
  }, [departamentosDoContexto]);

  const departamentoId = useMemo(
    () => toNumberOrNull(departamentoFinanceiro?.id),
    [departamentoFinanceiro]
  );

  const usuarioId = useMemo(
    () => toNumberOrNull(sessao?.id ?? sessao?.uid),
    [sessao]
  );

  const usuarioNome = sessao?.nome || "Usuário";

  const contextoPronto = Boolean(
    sessao && empresaId && enderecoId && departamentoId
  );

  /* ==========================================================================
   * STATES
   * ======================================================================== */

  const [ativos, setAtivos] = useState<AtivoMonitorado[]>([]);

  const [filters, setFilters] = useState<FilterState>(initialFilters);

  const [selectedAtivo, setSelectedAtivo] = useState<AtivoMonitorado | null>(
    null
  );

  const [novoAtivoOpen, setNovoAtivoOpen] = useState(false);

  const [novoAtivoForm, setNovoAtivoForm] = useState<NovoAtivoForm>(
    INITIAL_NEW_ASSET_FORM
  );

  const [apiError, setApiError] = useState<string | null>(null);

  const [apiSuccess, setApiSuccess] = useState<string | null>(null);

  const [addLoading, setAddLoading] = useState(false);

  const [syncAllLoading, setSyncAllLoading] = useState(false);

  const [syncItemLoadingId, setSyncItemLoadingId] = useState<number | null>(
    null
  );

  /* ==========================================================================
   * LIMPA QUANDO TROCA EMPRESA/ENDEREÇO
   * ======================================================================== */

  useEffect(() => {
    setAtivos([]);

    setSelectedAtivo(null);

    setApiError(null);

    setApiSuccess(null);
  }, [empresaId, enderecoId, departamentoId]);

  /* ==========================================================================
   * URL
   * ======================================================================== */

  const yahooQuotesUrl = `${apiBase}/template/financeiro/investimentos/yahoo/quotes`;

  /* ==========================================================================
   * POST PARA YAHOO
   * ======================================================================== */

  const postYahooQuotes = async (
    symbols: string[]
  ): Promise<YahooQuoteApiResponse> => {
    if (!sessao) {
      throw new Error("Sessão não carregada.");
    }

    if (!empresaId) {
      throw new Error("Empresa ativa não identificada na sessão.");
    }

    if (!enderecoId) {
      throw new Error("Endereço selecionado não identificado.");
    }

    if (!departamentoId) {
      throw new Error("Departamento Financeiro não encontrado.");
    }

    const payload = {
      usuario_id: usuarioId,

      empresa_id: empresaId,

      endereco_id: enderecoId,

      departamento_id: departamentoId,

      symbols: symbols.map(buildSymbolYahoo),
    };

    const makeRequest = async () => {
      return fetch(yahooQuotesUrl, {
        method: "POST",

        credentials: "include",

        cache: "no-store",

        headers: buildAuthenticatedHeaders(),

        body: JSON.stringify(payload),
      });
    };

    let response = await makeRequest();

    /*
     * Se a sessão estiver desatualizada,
     * tenta atualizar uma vez.
     */
    if (response.status === 401 || response.status === 403) {
      await refreshAuth();

      response = await makeRequest();
    }

    const data = (await response
      .json()
      .catch(() => null)) as YahooQuoteApiResponse | null;

    if (!response.ok || !data) {
      throw new Error(
        data?.message ||
          data?.erro ||
          `Falha HTTP ${response.status} ao consultar o Yahoo Finance.`
      );
    }

    if (data.ok === false) {
      const firstError = data.errors?.[0];

      throw new Error(
        firstError?.message ||
          data.message ||
          data.erro ||
          "Não foi possível consultar o Yahoo Finance."
      );
    }

    return data;
  };

  /* ==========================================================================
   * CONVERTE RESPOSTA YAHOO EM ATIVO
   * ======================================================================== */

  const buildAtivoFromQuote = (
    quote: YahooQuote,

    existing?: AtivoMonitorado,

    form?: NovoAtivoForm
  ): AtivoMonitorado => {
    const now = new Date().toISOString();

    const tipo =
      existing?.tipo ||
      form?.tipo ||
      inferTipoAtivoFromYahoo(quote.tipo_instrumento);

    const historicoAnterior = existing?.historico || [];

    return {
      id: existing?.id || createLocalId(),

      empresaId: empresaId || 0,

      empresaNome,

      ticker: normalizeTicker(quote.ticker || quote.symbol_yahoo),

      symbolYahoo: quote.symbol_yahoo,

      nome: quote.nome || quote.nome_curto || quote.ticker,

      nomeCurto: quote.nome_curto || null,

      tipo,

      tipoInstrumentoYahoo: quote.tipo_instrumento || null,

      mercado: quote.mercado || "B3",

      exchange: quote.exchange || null,

      exchangeCompleto: quote.exchange_completo || null,

      moeda: quote.moeda || "BRL",

      preco: quote.preco ?? null,

      fechamentoAnterior: quote.fechamento_anterior ?? null,

      variacao: quote.variacao ?? null,

      variacaoPercentual: quote.variacao_percentual ?? null,

      abertura: quote.abertura ?? null,

      maximaDia: quote.maxima_dia ?? null,

      minimaDia: quote.minima_dia ?? null,

      volume: quote.volume ?? null,

      maxima52Semanas: quote.maxima_52_semanas ?? null,

      minima52Semanas: quote.minima_52_semanas ?? null,

      horarioMercado: quote.horario_mercado ?? null,

      timezone: quote.timezone ?? null,

      consultadoEm: quote.consultado_em || now,

      fonte: "YAHOO_FINANCE",

      statusMonitoramento: existing?.statusMonitoramento || "ativo",

      observacao: existing?.observacao ?? form?.observacao?.trim() ?? null,

      alertaAcima: existing
        ? existing.alertaAcima
        : parseNumberInput(form?.alertaAcima || ""),

      alertaAbaixo: existing
        ? existing.alertaAbaixo
        : parseNumberInput(form?.alertaAbaixo || ""),

      historico: [
        {
          id: createLocalId(),

          data: quote.consultado_em || now,

          titulo: existing
            ? "Cotação sincronizada"
            : "Ativo consultado no Yahoo Finance",

          descricao: existing
            ? `Cotação de ${quote.ticker} atualizada pelo Yahoo Finance.`
            : `${quote.ticker} incluído após validação no Yahoo Finance.`,

          responsavel: usuarioNome,
        },

        ...historicoAnterior,
      ],
    };
  };

  /* ==========================================================================
   * FILTROS
   * ======================================================================== */

  const tiposDisponiveis = useMemo(() => {
    return Array.from(new Set(ativos.map((item) => item.tipo))).sort((a, b) =>
      a.localeCompare(b)
    );
  }, [ativos]);

  const filteredItems = useMemo(() => {
    const busca = normalizeTextSearch(filters.busca);

    return ativos.filter((ativo) => {
      const buscaMatch =
        !busca ||
        [
          ativo.ticker,
          ativo.symbolYahoo,
          ativo.nome,
          ativo.nomeCurto,
          ativo.tipoInstrumentoYahoo,
          ativo.exchange,
          ativo.exchangeCompleto,
          ativo.mercado,
          ativo.fonte,
          ativo.observacao,
        ].some((field) => normalizeTextSearch(field).includes(busca));

      const tipoMatch = !filters.tipo || ativo.tipo === filters.tipo;

      const statusMatch =
        !filters.statusMonitoramento ||
        ativo.statusMonitoramento === filters.statusMonitoramento;

      return buscaMatch && tipoMatch && statusMatch;
    });
  }, [ativos, filters]);

  /* ==========================================================================
   * RESUMO
   * ======================================================================== */

  const resumo = useMemo(() => {
    const monitoradosAtivos = ativos.filter(
      (item) => item.statusMonitoramento === "ativo"
    );

    const positivos = ativos.filter(
      (item) => item.variacaoPercentual !== null && item.variacaoPercentual > 0
    ).length;

    const negativos = ativos.filter(
      (item) => item.variacaoPercentual !== null && item.variacaoPercentual < 0
    ).length;

    const comAlertaDisparado = ativos.filter((item) =>
      isAlertaDisparado(item)
    ).length;

    const ultimaConsulta = ativos
      .map((item) => item.consultadoEm)
      .filter(Boolean)
      .sort((a, b) => String(b).localeCompare(String(a)))[0];

    return {
      total: ativos.length,

      monitoradosAtivos: monitoradosAtivos.length,

      positivos,

      negativos,

      comAlertaDisparado,

      ultimaConsulta: ultimaConsulta || null,
    };
  }, [ativos]);

  /* ==========================================================================
   * FORM
   * ======================================================================== */

  const updateFilter = <K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters(initialFilters);
  };

  const updateNovoAtivoForm = <K extends keyof NovoAtivoForm>(
    key: K,
    value: NovoAtivoForm[K]
  ) => {
    setNovoAtivoForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openNovoAtivo = () => {
    setApiError(null);

    setApiSuccess(null);

    setNovoAtivoForm(INITIAL_NEW_ASSET_FORM);

    setNovoAtivoOpen(true);
  };

  const closeNovoAtivo = () => {
    if (addLoading) {
      return;
    }

    setNovoAtivoOpen(false);
  };

  /* ==========================================================================
   * ADICIONAR
   * ======================================================================== */

  const handleAdicionarAtivo = async () => {
    if (!contextoPronto) {
      setApiError(
        "Empresa, endereço ou departamento Financeiro não foram identificados."
      );

      return;
    }

    const ticker = normalizeTicker(novoAtivoForm.ticker);

    if (!ticker) {
      setApiError("Informe o ticker do ativo.");

      return;
    }

    if (!/^[A-Z0-9]{4,15}$/.test(ticker)) {
      setApiError("Ticker inválido.");

      return;
    }

    const jaExiste = ativos.some((item) => item.ticker === ticker);

    if (jaExiste) {
      setApiError(`${ticker} já está na lista.`);

      return;
    }

    setAddLoading(true);

    setApiError(null);

    setApiSuccess(null);

    try {
      const data = await postYahooQuotes([buildSymbolYahoo(ticker)]);

      const quote = data.results?.[0];

      if (!quote) {
        throw new Error(
          data.errors?.[0]?.message || `Yahoo Finance não retornou ${ticker}.`
        );
      }

      const novoAtivo = buildAtivoFromQuote(quote, undefined, novoAtivoForm);

      setAtivos((prev) => [novoAtivo, ...prev]);

      setNovoAtivoOpen(false);

      setApiSuccess(
        `${novoAtivo.ticker} validado no Yahoo Finance e adicionado ao monitoramento.`
      );
    } catch (error) {
      setApiError(
        error instanceof Error
          ? error.message
          : "Erro ao consultar Yahoo Finance."
      );
    } finally {
      setAddLoading(false);
    }
  };

  /* ==========================================================================
   * SINCRONIZAR UM
   * ======================================================================== */

  const handleSincronizarAtivo = async (ativoId: number) => {
    const ativo = ativos.find((item) => item.id === ativoId);

    if (!ativo) {
      return;
    }

    setSyncItemLoadingId(ativoId);

    setApiError(null);

    setApiSuccess(null);

    try {
      const data = await postYahooQuotes([ativo.symbolYahoo]);

      const quote = data.results?.find(
        (item) =>
          item.symbol_yahoo === ativo.symbolYahoo ||
          normalizeTicker(item.ticker) === ativo.ticker
      );

      if (!quote) {
        throw new Error(
          data.errors?.[0]?.message ||
            `Cotação de ${ativo.ticker} não retornada.`
        );
      }

      const atualizado = buildAtivoFromQuote(quote, ativo);

      setAtivos((prev) =>
        prev.map((item) => (item.id === ativoId ? atualizado : item))
      );

      if (selectedAtivo?.id === ativoId) {
        setSelectedAtivo(atualizado);
      }

      setApiSuccess(`Cotação de ${ativo.ticker} atualizada.`);
    } catch (error) {
      /*
       * Mantém a última cotação.
       * Não inventa preço.
       */
      setApiError(
        error instanceof Error
          ? error.message
          : `Falha ao sincronizar ${ativo.ticker}.`
      );
    } finally {
      setSyncItemLoadingId(null);
    }
  };

  /* ==========================================================================
   * SINCRONIZAR TODOS
   * ======================================================================== */

  const handleSincronizarTodos = async () => {
    const ativosAtivos = ativos.filter(
      (item) => item.statusMonitoramento === "ativo"
    );

    if (!ativosAtivos.length) {
      setApiError("Não há ativos ativos para sincronizar.");

      return;
    }

    setSyncAllLoading(true);

    setApiError(null);

    setApiSuccess(null);

    try {
      const data = await postYahooQuotes(
        ativosAtivos.map((item) => item.symbolYahoo)
      );

      const quotes = data.results || [];

      const errors = data.errors || [];

      setAtivos((prev) =>
        prev.map((item) => {
          const quote = quotes.find(
            (q) =>
              q.symbol_yahoo === item.symbolYahoo ||
              normalizeTicker(q.ticker) === item.ticker
          );

          if (!quote) {
            return item;
          }

          return buildAtivoFromQuote(quote, item);
        })
      );

      if (selectedAtivo) {
        const quote = quotes.find(
          (q) =>
            q.symbol_yahoo === selectedAtivo.symbolYahoo ||
            normalizeTicker(q.ticker) === selectedAtivo.ticker
        );

        if (quote) {
          setSelectedAtivo(buildAtivoFromQuote(quote, selectedAtivo));
        }
      }

      if (quotes.length) {
        setApiSuccess(`${quotes.length} cotação(ões) atualizada(s).`);
      }

      if (errors.length) {
        setApiError(
          errors
            .map(
              (error) =>
                `${error.ticker || error.symbol_yahoo || "Ativo"}: ${
                  error.message || "falha"
                }`
            )
            .join(" | ")
        );
      }
    } catch (error) {
      setApiError(
        error instanceof Error
          ? error.message
          : "Falha ao sincronizar cotações."
      );
    } finally {
      setSyncAllLoading(false);
    }
  };

  /* ==========================================================================
   * PAUSAR / REATIVAR
   * ======================================================================== */

  const handleToggleMonitoramento = (ativoId: number) => {
    let atualizado: AtivoMonitorado | null = null;

    setAtivos((prev) =>
      prev.map((item) => {
        if (item.id !== ativoId) {
          return item;
        }

        const novoStatus: StatusMonitoramento =
          item.statusMonitoramento === "ativo" ? "pausado" : "ativo";

        atualizado = {
          ...item,

          statusMonitoramento: novoStatus,

          historico: [
            {
              id: createLocalId(),

              data: new Date().toISOString(),

              titulo:
                novoStatus === "ativo"
                  ? "Monitoramento reativado"
                  : "Monitoramento pausado",

              descricao:
                novoStatus === "ativo"
                  ? "Ativo reativado para acompanhamento."
                  : "Ativo pausado no monitoramento.",

              responsavel: usuarioNome,
            },

            ...item.historico,
          ],
        };

        return atualizado;
      })
    );

    if (selectedAtivo?.id === ativoId && atualizado) {
      setSelectedAtivo(atualizado);
    }
  };

  /* ==========================================================================
   * REMOVER
   * ======================================================================== */

  const handleRemoverAtivo = (ativoId: number) => {
    const ativo = ativos.find((item) => item.id === ativoId);

    if (!ativo) {
      return;
    }

    const confirmar = window.confirm(
      `Deseja remover ${ativo.ticker} da lista de monitoramento?`
    );

    if (!confirmar) {
      return;
    }

    setAtivos((prev) => prev.filter((item) => item.id !== ativoId));

    if (selectedAtivo?.id === ativoId) {
      setSelectedAtivo(null);
    }

    setApiSuccess(`${ativo.ticker} removido da lista.`);
  };

  /* ==========================================================================
   * AUTH
   * ======================================================================== */

  if (authLoading) {
    return (
      <div style={styles.page}>
        <TitleButtons fullBleedLine title="Ativos monitorados" />

        <div style={styles.centerState}>
          <RefreshCcw size={20} />

          <strong>Carregando sessão...</strong>
        </div>
      </div>
    );
  }

  if (!sessao) {
    return (
      <div style={styles.page}>
        <TitleButtons fullBleedLine title="Ativos monitorados" />

        <div style={styles.centerState}>
          <AlertTriangle size={20} />

          <strong>Sessão não autenticada.</strong>

          <span>{authError || "Entre novamente no LOGVI."}</span>
        </div>
      </div>
    );
  }

  /* ==========================================================================
   * RENDER
   * ======================================================================== */

  return (
    <div style={styles.page}>
      <TitleButtons fullBleedLine title="Ativos monitorados" />

      <div style={styles.content}>
        {/* ================================================================
         * CONTEXTO
         * ============================================================== */}

        <section style={styles.contextPanel}>
          <div style={styles.contextLeft}>
            <div style={styles.contextTitleRow}>
              <Landmark size={16} />

              <strong>Monitoramento de investimentos</strong>
            </div>

            <div style={styles.contextDescription}>
              Empresa: <strong>{empresaNome}</strong>
              {" · "}
              Usuário: <strong>{usuarioNome}</strong>
              {" · "}
              Fonte: <strong>Yahoo Finance</strong>
            </div>

            <div style={styles.contextMeta}>
              Empresa ID: {empresaId ?? "—"}
              {" · "}
              Endereço ID: {enderecoId ?? "—"}
              {" · "}
              Financeiro ID: {departamentoId ?? "não encontrado"}
            </div>
          </div>

          <div style={styles.contextActions}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={handleSincronizarTodos}
              disabled={syncAllLoading || !ativos.length || !contextoPronto}
            >
              <RefreshCcw size={16} />

              {syncAllLoading ? "Sincronizando..." : "Sincronizar cotações"}
            </button>

            <button
              type="button"
              style={{
                ...styles.primaryButton,

                background: activeDepartmentColor,
              }}
              onClick={openNovoAtivo}
              disabled={!contextoPronto}
            >
              <Plus size={16} />
              Adicionar ativo
            </button>
          </div>
        </section>

        {!contextoPronto ? (
          <div style={styles.warningAlert}>
            <AlertTriangle size={16} />

            <span>
              Não foi possível identificar completamente o contexto atual.
              Empresa: {empresaId ?? "—"}, endereço: {enderecoId ?? "—"},
              departamento Financeiro: {departamentoId ?? "—"}.
            </span>
          </div>
        ) : null}

        {apiError ? (
          <div style={styles.errorAlert}>
            <AlertTriangle size={16} />

            <span>{apiError}</span>
          </div>
        ) : null}

        {apiSuccess ? (
          <div style={styles.successAlert}>
            <CheckCircle2 size={16} />

            <span>{apiSuccess}</span>
          </div>
        ) : null}

        {/* ================================================================
         * RESUMO
         * ============================================================== */}

        <section style={styles.summaryGrid}>
          <SummaryCard
            label="Ativos cadastrados"
            value={resumo.total}
            helper={`${resumo.monitoradosAtivos} em monitoramento ativo`}
            tone="neutral"
            icon={<Layers3 size={16} />}
          />

          <SummaryCard
            label="Variação positiva"
            value={resumo.positivos}
            helper={`${resumo.negativos} ativo(s) em queda`}
            tone="success"
            icon={<TrendingUp size={16} />}
          />

          <SummaryCard
            label="Alertas disparados"
            value={resumo.comAlertaDisparado}
            helper="Com base nos limites configurados"
            tone={resumo.comAlertaDisparado > 0 ? "warning" : "neutral"}
            icon={<BellRing size={16} />}
          />

          <SummaryCard
            label="Última sincronização"
            value={
              resumo.ultimaConsulta
                ? formatDateTime(resumo.ultimaConsulta)
                : "Sem consulta"
            }
            helper="Yahoo Finance"
            tone="neutral"
            icon={<Clock3 size={16} />}
          />
        </section>

        {/* ================================================================
         * FILTROS
         * ============================================================== */}

        <section style={styles.filtersCard}>
          <div style={styles.filtersHeader}>
            <div style={styles.filtersTitle}>
              <Filter size={16} />

              <strong>Filtros</strong>
            </div>

            <button
              type="button"
              style={styles.clearButton}
              onClick={clearFilters}
            >
              Limpar filtros
            </button>
          </div>

          <div style={styles.filtersGrid}>
            <label style={styles.fieldBlock}>
              <span style={styles.fieldLabel}>Buscar</span>

              <div style={styles.searchInputWrap}>
                <Search size={15} style={styles.searchIcon} />

                <input
                  style={styles.searchInput}
                  placeholder="Ticker, empresa, instrumento..."
                  value={filters.busca}
                  onChange={(event) =>
                    updateFilter("busca", event.target.value)
                  }
                />
              </div>
            </label>

            <label style={styles.fieldBlock}>
              <span style={styles.fieldLabel}>Tipo</span>

              <select
                style={styles.select}
                value={filters.tipo}
                onChange={(event) => updateFilter("tipo", event.target.value)}
              >
                <option value="">Todos</option>

                {tiposDisponiveis.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {getTipoAtivoLabel(tipo)}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.fieldBlock}>
              <span style={styles.fieldLabel}>Status</span>

              <select
                style={styles.select}
                value={filters.statusMonitoramento}
                onChange={(event) =>
                  updateFilter("statusMonitoramento", event.target.value)
                }
              >
                <option value="">Todos</option>

                <option value="ativo">Ativo</option>

                <option value="pausado">Pausado</option>
              </select>
            </label>
          </div>
        </section>

        {/* ================================================================
         * TABELA
         * ============================================================== */}

        <section style={styles.tableSection}>
          <div style={styles.tableHeader}>
            <div>
              <strong>Lista de ativos monitorados</strong>

              <div style={styles.tableHelper}>
                {filteredItems.length} item(ns)
              </div>
            </div>
          </div>

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Ativo</th>

                  <th style={styles.th}>Símbolo</th>

                  <th style={styles.th}>Cotação</th>

                  <th style={styles.th}>Variação</th>

                  <th style={styles.th}>Dia</th>

                  <th style={styles.th}>Volume</th>

                  <th style={styles.th}>Status</th>

                  <th style={styles.th}>Alerta</th>

                  <th style={styles.th}>Atualização</th>

                  <th style={styles.thNoRightBorder}>Abrir</th>
                </tr>
              </thead>

              <tbody>
                {filteredItems.length ? (
                  filteredItems.map((ativo) => (
                    <AtivoRow
                      key={ativo.id}
                      ativo={ativo}
                      departmentColor={activeDepartmentColor}
                      onOpen={setSelectedAtivo}
                    />
                  ))
                ) : (
                  <tr>
                    <td style={styles.emptyTd} colSpan={10}>
                      <div style={styles.emptyState}>
                        <BarChart3 size={20} />

                        <strong>Nenhum ativo monitorado</strong>

                        <span>
                          Clique em "Adicionar ativo" e informe um ticker da B3,
                          por exemplo WEGE3.
                        </span>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* ==================================================================
       * DRAWER
       * ================================================================ */}

      {selectedAtivo ? (
        <div
          style={styles.drawerOverlay}
          onClick={() => setSelectedAtivo(null)}
        >
          <aside
            style={styles.drawer}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.drawerHeader}>
              <div>
                <div style={styles.drawerTitleRow}>
                  <strong style={styles.drawerTitle}>
                    {selectedAtivo.ticker}
                  </strong>

                  <Badge
                    background={
                      getStatusMonitoramentoVisual(
                        selectedAtivo.statusMonitoramento
                      ).background
                    }
                    color={
                      getStatusMonitoramentoVisual(
                        selectedAtivo.statusMonitoramento
                      ).color
                    }
                    border={
                      getStatusMonitoramentoVisual(
                        selectedAtivo.statusMonitoramento
                      ).border
                    }
                    icon={
                      getStatusMonitoramentoVisual(
                        selectedAtivo.statusMonitoramento
                      ).icon
                    }
                  >
                    {getStatusMonitoramentoLabel(
                      selectedAtivo.statusMonitoramento
                    )}
                  </Badge>
                </div>

                <div style={styles.drawerSubtitle}>{selectedAtivo.nome}</div>
              </div>

              <button
                type="button"
                style={styles.iconCloseButton}
                onClick={() => setSelectedAtivo(null)}
              >
                <X size={18} />
              </button>
            </div>

            <div style={styles.drawerBody}>
              <section style={styles.detailCard}>
                <div style={styles.detailHeader}>
                  <strong>Cotação</strong>
                </div>

                <div style={styles.detailGrid}>
                  <Detail label="Ticker" value={selectedAtivo.ticker} />

                  <Detail label="Yahoo" value={selectedAtivo.symbolYahoo} />

                  <Detail label="Nome" value={selectedAtivo.nome} />

                  <Detail
                    label="Tipo Yahoo"
                    value={selectedAtivo.tipoInstrumentoYahoo || "—"}
                  />

                  <Detail
                    label="Preço"
                    value={formatCurrency(
                      selectedAtivo.preco,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Variação"
                    value={formatPercent(selectedAtivo.variacaoPercentual)}
                  />

                  <Detail
                    label="Abertura"
                    value={formatCurrency(
                      selectedAtivo.abertura,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Fechamento anterior"
                    value={formatCurrency(
                      selectedAtivo.fechamentoAnterior,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Mínima do dia"
                    value={formatCurrency(
                      selectedAtivo.minimaDia,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Máxima do dia"
                    value={formatCurrency(
                      selectedAtivo.maximaDia,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Mínima 52 semanas"
                    value={formatCurrency(
                      selectedAtivo.minima52Semanas,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Máxima 52 semanas"
                    value={formatCurrency(
                      selectedAtivo.maxima52Semanas,
                      selectedAtivo.moeda
                    )}
                  />

                  <Detail
                    label="Volume"
                    value={formatInteger(selectedAtivo.volume)}
                  />

                  <Detail
                    label="Exchange"
                    value={
                      selectedAtivo.exchangeCompleto ||
                      selectedAtivo.exchange ||
                      "—"
                    }
                  />

                  <Detail
                    label="Horário mercado"
                    value={formatDateTime(selectedAtivo.horarioMercado)}
                  />

                  <Detail
                    label="Consultado em"
                    value={formatDateTime(selectedAtivo.consultadoEm)}
                  />
                </div>
              </section>

              <section style={styles.detailCard}>
                <div style={styles.detailHeader}>
                  <strong>Monitoramento</strong>
                </div>

                <div style={styles.detailGrid}>
                  <Detail label="Empresa" value={selectedAtivo.empresaNome} />

                  <Detail label="Usuário" value={usuarioNome} />

                  <Detail
                    label="Alerta acima"
                    value={
                      selectedAtivo.alertaAcima !== null
                        ? formatCurrency(
                            selectedAtivo.alertaAcima,
                            selectedAtivo.moeda
                          )
                        : "Não configurado"
                    }
                  />

                  <Detail
                    label="Alerta abaixo"
                    value={
                      selectedAtivo.alertaAbaixo !== null
                        ? formatCurrency(
                            selectedAtivo.alertaAbaixo,
                            selectedAtivo.moeda
                          )
                        : "Não configurado"
                    }
                  />
                </div>

                {selectedAtivo.observacao ? (
                  <div style={styles.noteBox}>{selectedAtivo.observacao}</div>
                ) : null}
              </section>

              <section style={styles.detailCard}>
                <div style={styles.detailHeader}>
                  <strong>Histórico</strong>
                </div>

                <div style={styles.timeline}>
                  {selectedAtivo.historico.map((evento) => (
                    <div key={evento.id} style={styles.timelineItem}>
                      <div style={styles.timelineBullet} />

                      <div style={styles.timelineContent}>
                        <div style={styles.timelineTop}>
                          <strong>{evento.titulo}</strong>

                          <span style={styles.timelineDate}>
                            {formatDateTime(evento.data)}
                          </span>
                        </div>

                        <div style={styles.timelineDescription}>
                          {evento.descricao}
                        </div>

                        <div style={styles.timelineResponsible}>
                          Responsável: {evento.responsavel}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            <div style={styles.drawerFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => handleSincronizarAtivo(selectedAtivo.id)}
                disabled={syncItemLoadingId === selectedAtivo.id}
              >
                <RefreshCcw size={16} />

                {syncItemLoadingId === selectedAtivo.id
                  ? "Sincronizando..."
                  : "Sincronizar"}
              </button>

              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => handleToggleMonitoramento(selectedAtivo.id)}
              >
                <Activity size={16} />

                {selectedAtivo.statusMonitoramento === "ativo"
                  ? "Pausar"
                  : "Reativar"}
              </button>

              <button
                type="button"
                style={styles.dangerButton}
                onClick={() => handleRemoverAtivo(selectedAtivo.id)}
              >
                <Trash2 size={16} />
                Remover
              </button>
            </div>
          </aside>
        </div>
      ) : null}

      {/* ==================================================================
       * MODAL NOVO ATIVO
       * ================================================================ */}

      {novoAtivoOpen ? (
        <div style={styles.modalOverlay} onClick={closeNovoAtivo}>
          <div
            style={styles.modal}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <strong style={styles.modalTitle}>Adicionar ativo</strong>

                <div style={styles.modalSubtitle}>
                  O ticker será validado diretamente no Yahoo Finance.
                </div>
              </div>

              <button
                type="button"
                style={styles.iconCloseButton}
                onClick={closeNovoAtivo}
              >
                <X size={18} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGrid}>
                <label style={styles.fieldBlock}>
                  <span style={styles.fieldLabel}>Ticker *</span>

                  <input
                    style={styles.input}
                    placeholder="Ex.: WEGE3"
                    value={novoAtivoForm.ticker}
                    onChange={(event) =>
                      updateNovoAtivoForm(
                        "ticker",
                        event.target.value.toUpperCase()
                      )
                    }
                  />

                  <span style={styles.fieldHelp}>
                    Pode informar WEGE3 ou WEGE3.SA.
                  </span>
                </label>

                <label style={styles.fieldBlock}>
                  <span style={styles.fieldLabel}>Classificação interna</span>

                  <select
                    style={styles.select}
                    value={novoAtivoForm.tipo}
                    onChange={(event) =>
                      updateNovoAtivoForm(
                        "tipo",
                        event.target.value as TipoAtivo
                      )
                    }
                  >
                    <option value="ACAO">Ação</option>

                    <option value="FII">FII</option>

                    <option value="ETF">ETF</option>

                    <option value="BDR">BDR</option>

                    <option value="OUTRO">Outro</option>
                  </select>
                </label>

                <label style={styles.fieldBlock}>
                  <span style={styles.fieldLabel}>Alerta acima de</span>

                  <input
                    style={styles.input}
                    placeholder="Ex.: 60,00"
                    value={novoAtivoForm.alertaAcima}
                    onChange={(event) =>
                      updateNovoAtivoForm("alertaAcima", event.target.value)
                    }
                  />
                </label>

                <label style={styles.fieldBlock}>
                  <span style={styles.fieldLabel}>Alerta abaixo de</span>

                  <input
                    style={styles.input}
                    placeholder="Ex.: 45,00"
                    value={novoAtivoForm.alertaAbaixo}
                    onChange={(event) =>
                      updateNovoAtivoForm("alertaAbaixo", event.target.value)
                    }
                  />
                </label>

                <label
                  style={{
                    ...styles.fieldBlock,
                    gridColumn: "1 / -1",
                  }}
                >
                  <span style={styles.fieldLabel}>Observação interna</span>

                  <textarea
                    style={styles.textarea}
                    placeholder="Motivo do monitoramento, tese interna, observações..."
                    value={novoAtivoForm.observacao}
                    onChange={(event) =>
                      updateNovoAtivoForm("observacao", event.target.value)
                    }
                  />
                </label>
              </div>

              <div style={styles.yahooInfoBox}>
                <BarChart3 size={16} />

                <div>
                  <strong>Dados automáticos</strong>

                  <p>
                    Nome, preço, fechamento anterior, variação, abertura,
                    mínima, máxima, volume e informações de mercado serão
                    obtidos pelo backend do LOGVI através do Yahoo Finance.
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={closeNovoAtivo}
                disabled={addLoading}
              >
                Cancelar
              </button>

              <button
                type="button"
                style={{
                  ...styles.primaryButton,

                  background: activeDepartmentColor,
                }}
                onClick={handleAdicionarAtivo}
                disabled={addLoading || !contextoPronto}
              >
                <Plus size={16} />

                {addLoading
                  ? "Consultando Yahoo..."
                  : "Adicionar ao monitoramento"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

/* ============================================================================
 * STYLES
 * ========================================================================== */

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "#f4f7fb",
  },

  content: {
    padding: "20px 24px 28px",
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  centerState: {
    minHeight: 260,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: 10,
    color: "#475569",
  },

  contextPanel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    background: "#ffffff",
    padding: "16px 18px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  },

  contextLeft: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  contextTitleRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#0f172a",
    fontSize: 15,
  },

  contextDescription: {
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.5,
  },

  contextMeta: {
    color: "#94a3b8",
    fontSize: 11,
    lineHeight: 1.5,
  },

  contextActions: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },

  primaryButton: {
    border: "none",
    color: "#ffffff",
    minHeight: 40,
    borderRadius: 10,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontWeight: 700,
    cursor: "pointer",
    boxShadow: "0 8px 16px rgba(15, 23, 42, 0.08)",
  },

  secondaryButton: {
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#0f172a",
    minHeight: 40,
    borderRadius: 10,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontWeight: 600,
    cursor: "pointer",
  },

  dangerButton: {
    border: "1px solid #fecaca",
    background: "#fff1f2",
    color: "#b91c1c",
    minHeight: 40,
    borderRadius: 10,
    padding: "0 14px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontWeight: 700,
    cursor: "pointer",
  },

  successAlert: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#ecfdf3",
    border: "1px solid #b7ebc6",
    color: "#166534",
    padding: "12px 14px",
    borderRadius: 12,
    fontSize: 13,
  },

  errorAlert: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    color: "#991b1b",
    padding: "12px 14px",
    borderRadius: 12,
    fontSize: 13,
  },

  warningAlert: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    padding: "12px 14px",
    borderRadius: 12,
    fontSize: 13,
  },

  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },

  summaryCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
    display: "flex",
    alignItems: "flex-start",
    gap: 14,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  },

  summaryIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "#eff6ff",
    color: "#1d4ed8",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconSuccess: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "#ecfdf3",
    color: "#166534",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconWarning: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "#fff7ed",
    color: "#9a3412",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryIconDanger: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "#fef2f2",
    color: "#991b1b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  summaryInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },

  summaryLabel: {
    fontSize: 13,
    color: "#64748b",
  },

  summaryValue: {
    fontSize: 21,
    color: "#0f172a",
    lineHeight: 1.15,
  },

  summaryHelper: {
    fontSize: 12,
    color: "#64748b",
    lineHeight: 1.4,
  },

  filtersCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  },

  filtersHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },

  filtersTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    color: "#0f172a",
    fontSize: 14,
  },

  clearButton: {
    border: "none",
    background: "transparent",
    color: "#475569",
    fontWeight: 600,
    cursor: "pointer",
  },

  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
  },

  fieldBlock: {
    display: "flex",
    flexDirection: "column",
    gap: 7,
  },

  fieldLabel: {
    fontSize: 12,
    color: "#475569",
    fontWeight: 600,
  },

  fieldHelp: {
    fontSize: 11,
    color: "#94a3b8",
  },

  searchInputWrap: {
    position: "relative",
  },

  searchIcon: {
    position: "absolute",
    top: "50%",
    left: 12,
    transform: "translateY(-50%)",
    color: "#94a3b8",
  },

  searchInput: {
    width: "100%",
    height: 40,
    borderRadius: 10,
    border: "1px solid #dbe4ef",
    padding: "0 12px 0 36px",
    fontSize: 13,
    outline: "none",
    background: "#ffffff",
    boxSizing: "border-box",
  },

  input: {
    width: "100%",
    height: 40,
    borderRadius: 10,
    border: "1px solid #dbe4ef",
    padding: "0 12px",
    fontSize: 13,
    outline: "none",
    background: "#ffffff",
    boxSizing: "border-box",
  },

  select: {
    width: "100%",
    height: 40,
    borderRadius: 10,
    border: "1px solid #dbe4ef",
    padding: "0 12px",
    fontSize: 13,
    outline: "none",
    background: "#ffffff",
    boxSizing: "border-box",
  },

  textarea: {
    width: "100%",
    minHeight: 100,
    borderRadius: 12,
    border: "1px solid #dbe4ef",
    padding: 12,
    fontSize: 13,
    outline: "none",
    background: "#ffffff",
    resize: "vertical",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },

  yahooInfoBox: {
    marginTop: 18,
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    padding: 14,
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#475569",
    fontSize: 12,
  },

  tableSection: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  },

  tableHeader: {
    padding: "16px 18px 12px",
    borderBottom: "1px solid #edf2f7",
  },

  tableHelper: {
    fontSize: 12,
    color: "#64748b",
    marginTop: 4,
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 1350,
  },

  th: {
    textAlign: "left",
    fontSize: 12,
    color: "#64748b",
    fontWeight: 700,
    padding: "12px 14px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    borderRight: "1px solid #eef2f7",
    whiteSpace: "nowrap",
  },

  thNoRightBorder: {
    textAlign: "left",
    fontSize: 12,
    color: "#64748b",
    fontWeight: 700,
    padding: "12px 14px",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  tr: {},

  td: {
    padding: "14px",
    borderBottom: "1px solid #eef2f7",
    borderRight: "1px solid #f3f6fa",
    verticalAlign: "top",
  },

  tdNoRightBorder: {
    padding: "14px",
    borderBottom: "1px solid #eef2f7",
    verticalAlign: "top",
  },

  emptyTd: {
    padding: 32,
  },

  emptyState: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    color: "#64748b",
    minHeight: 180,
    textAlign: "center",
  },

  assetCell: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
  },

  assetIcon: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid #dbe4ef",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  assetInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
  },

  assetTicker: {
    fontSize: 14,
  },

  mainText: {
    color: "#111827",
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.4,
  },

  valueText: {
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.4,
  },

  mutedText: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.4,
  },

  smallMeta: {
    color: "#94a3b8",
    fontSize: 11,
    lineHeight: 1.4,
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "1px solid transparent",
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },

  inlineIconButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    border: "1px solid #dbe4ef",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },

  drawerOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.44)",
    display: "flex",
    justifyContent: "flex-end",
    zIndex: 1200,
  },

  drawer: {
    width: 600,
    maxWidth: "100%",
    height: "100%",
    background: "#ffffff",
    boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.16)",
    display: "flex",
    flexDirection: "column",
  },

  drawerHeader: {
    padding: "18px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },

  drawerTitleRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 4,
  },

  drawerTitle: {
    fontSize: 22,
    color: "#0f172a",
  },

  drawerSubtitle: {
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
  },

  iconCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },

  drawerBody: {
    flex: 1,
    overflowY: "auto",
    padding: 18,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  drawerFooter: {
    padding: 18,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },

  detailCard: {
    border: "1px solid #e2e8f0",
    borderRadius: 14,
    background: "#ffffff",
    overflow: "hidden",
  },

  detailHeader: {
    padding: "14px 16px",
    borderBottom: "1px solid #eef2f7",
    background: "#f8fafc",
    color: "#0f172a",
    fontSize: 14,
  },

  detailGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
    padding: 16,
  },

  detailItem: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
  },

  detailLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 600,
  },

  detailValue: {
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.45,
  },

  noteBox: {
    margin: "0 16px 16px",
    padding: 14,
    borderRadius: 10,
    background: "#f8fafc",
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.6,
  },

  timeline: {
    padding: 16,
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },

  timelineItem: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
  },

  timelineBullet: {
    width: 10,
    height: 10,
    borderRadius: 999,
    background: DEPARTMENT_COLOR,
    marginTop: 6,
    flexShrink: 0,
  },

  timelineContent: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    minWidth: 0,
    flex: 1,
  },

  timelineTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
    color: "#0f172a",
    fontSize: 13,
  },

  timelineDate: {
    color: "#64748b",
    fontSize: 12,
  },

  timelineDescription: {
    color: "#334155",
    fontSize: 13,
    lineHeight: 1.55,
  },

  timelineResponsible: {
    color: "#64748b",
    fontSize: 12,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.44)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    zIndex: 1250,
  },

  modal: {
    width: 720,
    maxWidth: "100%",
    background: "#ffffff",
    borderRadius: 18,
    border: "1px solid #e2e8f0",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    maxHeight: "90vh",
  },

  modalHeader: {
    padding: "18px 20px 16px",
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },

  modalTitle: {
    fontSize: 18,
    color: "#0f172a",
  },

  modalSubtitle: {
    color: "#64748b",
    fontSize: 13,
    marginTop: 4,
    lineHeight: 1.5,
  },

  modalBody: {
    padding: 20,
    overflowY: "auto",
  },

  modalFooter: {
    padding: 20,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },

  formGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 14,
  },
};

export default InvestimentosAtivosMonitoradosLayout;
